import {
  type QueryFilter,
  type PerformanceWithAccountFilter,
  completes,
  createDateRange,
  dailyActivity,
  lineChartFactory,
  lineChartTitles,
  noBarData,
  noData,
  totalActivity,
  validLineData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type FC, useEffect, useMemo, useState } from "react";

import { ChartWrapper, HorizontalBarChart, LineChart, getDateFormatByCount } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { useQuery, type QueryFunctionContext } from "@tanstack/react-query";
import { getPdfsDailyData, getPdfsEngagement, getPdfsTable } from "../../../services/contentReportingService";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";

import { GenericPerformanceList } from "../../shared/GenericPerformanceList";
import { type ColumnOption } from "interfaces/columnOptions";
import { type PdfDetails } from "features/Reporting/types/content";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import dateTimeUtils from "utils/dateTimeUtils";
import { mockEmptyPdfEngagementData } from "features/Reporting/mocks/contentData";
import { type CommonAccountReportPropsExtended } from "../../types";
import { EntityType } from "features/Library/Flows/Designer/types";
import { sharedAccountReportingHorizontalBarProps, sharedAccountReportingLineProps } from "../../shared";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindActionCreators } from "@reduxjs/toolkit";
import { doPdfExport } from "features/Reporting/state/content/contentActions";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { useAccountFilterShouldShow } from "../../queries/useAccountFilterShouldShow";

import styles from "../../contentOverviewReports.module.scss";
import "features/Library/Common/utils/performanceSCSSUtils.scss";

export type Props = CommonAccountReportPropsExtended & PropsFromRedux;

const fetchDailyData = async ({ queryKey }: QueryFilter) => {
  const { data } = await getPdfsDailyData(queryKey[1]);

  return lineChartFactory(data);
};

type EngagementQueryContext = QueryFunctionContext<
  [_: string, filter: PerformanceWithAccountFilter, showAccounts?: boolean],
  unknown
>;

const fetchEngagementData = async ({ queryKey }: EngagementQueryContext) => {
  const apiFilter = queryKey[1];
  const showAccounts = queryKey[2];

  let result = (await getPdfsEngagement(apiFilter)).data;
  result = {
    ...mockEmptyPdfEngagementData,
    ...result,
  };
  const usageBarData: DataPoint[] = [
    {
      id: completes.toLowerCase(),
      category: completes,
      value: result.CompletedCount,
      fillColor: reportingColors.yellow,
    },
  ];

  const cardData: CardReportingItem[] = [];

  if (showAccounts) {
    cardData.push({
      statDescription: "Accounts",
      stat: result.Customers,
      popupBody: "The total number of customers across all PDFs during the specified time period.",
    });
  }

  cardData.push(
    {
      statDescription: "PDFs",
      stat: result.PDFs,
      popupBody: "The total number of PDFs across all accounts within the selected time period.",
    },
    {
      statDescription: "Completes",
      stat: result.CompletedCount,
      popupBody: "The total number of PDF completions across all accounts within the selected time period.",
    },
  );

  return { barData: usageBarData, cardData };
};

const fetchTableData = async ({ queryKey }: QueryFilter) => {
  const result = (await getPdfsTable(queryKey[1])).data;

  return result.map((item) => {
    return {
      ...item,
      id: `${item.PdfId}_${item.AccountId}`,
    };
  });
};

export const PdfReport: FC<Props> = ({
  filter,
  setSelectedContent,
  setHasExportData,
  setExportAction,
  pdfExport,
  setIsReportEnabled,
  resetIsReportEnabled,
  accountId,
  accountType,
  disableLinks,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = useFeatureFlag(FeatureFlags.PdfOverviewEndpoints);
  const showAccounts = useAccountFilterShouldShow(accountId, accountType);

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["AccountPdfLineReport", { ...filter, aggregation: chartPeriod }],
    queryFn: fetchDailyData,
    enabled: reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["AccountPdfEngagementReport", filter, showAccounts],
    queryFn: fetchEngagementData,
    enabled: reportEnabled && filter.type !== undefined && showAccounts !== undefined,
  });

  const detailsQuery = useQuery({
    queryKey: ["AccountPdfDetailsReport", filter],
    queryFn: fetchTableData,
    enabled: reportEnabled && filter.type !== undefined,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!detailsQuery.data && detailsQuery.data.length > 0,
      tooltipContent: "No data available for export. There are no PDFs with activity data.",
    });
  }, [detailsQuery.data, setHasExportData]);

  // Setting up correct export method if send entity is changed
  useEffect(() => {
    setExportAction({
      method: pdfExport,
      args: [filter],
      isExportEnabled: true,
    });
  }, [pdfExport, filter, setExportAction]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const lineData = useMemo(() => {
    if (chartState === dailyActivity) {
      return {
        xData: [lineChartQuery.data?.Date],
        yData: [lineChartQuery.data?.CompletedCount],
      };
    }
    return {
      xData: [lineChartQuery.data?.Date],
      yData: [lineChartQuery.data?.CumulativeCompletedCount],
    };
  }, [
    chartState,
    lineChartQuery.data?.CompletedCount,
    lineChartQuery.data?.CumulativeCompletedCount,
    lineChartQuery.data?.Date,
  ]);

  const columns: ColumnOption<PdfDetails>[] = useMemo(
    () => [
      {
        name: "PDF",
        width: 7,
        isSortable: false,
        render(item) {
          const titleContent = (
            <TextTruncate lines={2} className={styles.wordBreak}>
              {item.PdfTitle}
            </TextTruncate>
          );
          const shouldShowLink = !filter.showCustomers && !disableLinks;
          return (
            <Tooltip
              target={
                shouldShowLink ? (
                  <button
                    className={styles.linkButton}
                    onClick={() => setSelectedContent({ id: item.PdfId, type: EntityType.Pdf, name: item.PdfTitle })}
                  >
                    {titleContent}
                  </button>
                ) : (
                  titleContent
                )
              }
              content={item.PdfTitle}
              showAlways
            />
          );
        },
      },
      {
        name: `Total ${completes}`,
        width: 7,
        isSortable: false,
        render(item) {
          return <div>{item.CompletedCount.toLocaleString()}</div>;
        },
      },
      {
        name: "Last Activity",
        width: 7,
        isSortable: false,
        render(item) {
          return <div>{dateTimeUtils.formatDate(item.LastActivityDate)}</div>;
        },
      },
    ],
    [setSelectedContent, filter.showCustomers, disableLinks],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          colorRange={[reportingColors.yellow]}
          showLegend
          onChange={setChartState}
          legendLabels={[completes]}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([lineChartQuery.data.CompletedCount, lineChartQuery.data.CumulativeCompletedCount]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                {...lineData}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                colorRange={[reportingColors.yellow]}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d) => d.value)) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.performanceCardSection}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cardData} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <RequestStatusRenderer state={detailsQuery.status}>
          {detailsQuery.isSuccess && (
            <GenericPerformanceList title="PDFs" rows={detailsQuery.data} columns={columns} filter={filter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  pdfExport: bindActionCreators(doPdfExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PdfReport);
export default ConnectedComponent;
